import { MaterialCommunityIcons } from '@expo/vector-icons';
import React from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import theme from '@/config/theme';
import type { AppRecoveryRuntimeSnapshot } from '@/services/recovery/appRecoveryRuntime';
import { normalizeRecoveryUserError } from '@/services/recovery/recoverySafeErrors';
import type { RecoveryRuntimeGateSnapshot } from '@/services/recovery/RecoveryRuntimeGate';

interface RecoveryGateScreenProps {
  gate: RecoveryRuntimeGateSnapshot;
  runtime: AppRecoveryRuntimeSnapshot;
  onConsent(): void;
  onResume(): void;
  onVerify(): void;
}

export interface RecoveryGatePresentation {
  title: string;
  message: string;
  showProgress: boolean;
  showConsent: boolean;
  showResume: boolean;
  showVerify: boolean;
  showSpinner: boolean;
}

export const getRecoveryGatePresentation = (
  gate: RecoveryRuntimeGateSnapshot,
  runtime: AppRecoveryRuntimeSnapshot
): RecoveryGatePresentation => {
  if (gate.status === 'checking' || runtime.status === 'idle') {
    return {
      title: 'Preparando o aplicativo',
      message: 'Estamos verificando a conexão e os dados deste aparelho. Isso pode levar alguns instantes. Sem conexão, o uso offline será liberado após a verificação dos dados locais. Por favor, aguarde sem fechar o aplicativo.',
      showProgress: false,
      showConsent: false,
      showResume: false,
      showVerify: false,
      showSpinner: true,
    };
  }
  if (runtime.status === 'consent_required') {
    return {
      title: 'Preservacao de dados necessaria',
      message: 'A sincronizacao esta pausada. Seus dados continuam neste aparelho ate a preservacao segura terminar.',
      showProgress: false,
      showConsent: true,
      showResume: false,
      showVerify: false,
      showSpinner: false,
    };
  }
  if (runtime.status === 'awaiting_validation') {
    return {
      title: 'Pacote enviado com seguranca',
      message: runtime.message,
      showProgress: true,
      showConsent: false,
      showResume: false,
      showVerify: true,
      showSpinner: false,
    };
  }
  if (runtime.status === 'error') {
    return {
      title: 'Preservacao pausada',
      message: normalizeRecoveryUserError(runtime.error ?? runtime.message),
      showProgress: runtime.progress > 0,
      showConsent: runtime.canConsent,
      showResume: runtime.canResume,
      showVerify: runtime.canVerify,
      showSpinner: false,
    };
  }
  return {
    title: runtime.status === 'completed'
      ? 'Recuperacao concluida'
      : 'Preservando dados locais',
    message: runtime.message,
    showProgress: true,
    showConsent: false,
    showResume: false,
    showVerify: runtime.canVerify && !runtime.busy,
    showSpinner: runtime.busy,
  };
};

const RecoveryAction = ({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress(): void;
  disabled: boolean;
}) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    disabled={disabled}
    onPress={onPress}
    style={({ pressed }) => [
      styles.action,
      pressed && !disabled && styles.actionPressed,
      disabled && styles.actionDisabled,
    ]}
  >
    <Text style={styles.actionText}>{label}</Text>
  </Pressable>
);

export const RecoveryGateScreen = ({
  gate,
  runtime,
  onConsent,
  onResume,
  onVerify,
}: RecoveryGateScreenProps) => {
  const presentation = getRecoveryGatePresentation(gate, runtime);
  return (
    <View style={styles.container} accessibilityRole="alert">
      <View style={styles.content}>
        <MaterialCommunityIcons
          name="shield-lock-outline"
          size={44}
          color={theme.colors.primary.default}
        />
        <Text style={styles.title}>{presentation.title}</Text>
        <Text style={styles.message}>{presentation.message}</Text>

        {presentation.showProgress && (
          <View style={styles.progressTrack} accessibilityLabel={`Progresso ${runtime.progress}%`}>
            <View style={[styles.progressFill, { width: `${runtime.progress}%` }]} />
          </View>
        )}

        {presentation.showSpinner && (
          <ActivityIndicator size="small" color={theme.colors.primary.default} />
        )}

        <View style={styles.actions}>
          {presentation.showConsent && (
            <RecoveryAction
              label="Preservar dados e continuar"
              onPress={onConsent}
              disabled={runtime.busy}
            />
          )}
          {presentation.showResume && (
            <RecoveryAction
              label="Retomar preservacao"
              onPress={onResume}
              disabled={runtime.busy}
            />
          )}
          {presentation.showVerify && (
            <RecoveryAction
              label="Verificar preservacao"
              onPress={onVerify}
              disabled={runtime.busy}
            />
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
    backgroundColor: theme.colors.background.secondary,
  },
  content: {
    width: '100%',
    maxWidth: 480,
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.text.primary,
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  progressTrack: {
    width: '100%',
    height: 8,
    overflow: 'hidden',
    borderRadius: 4,
    backgroundColor: theme.colors.border.light,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
    backgroundColor: theme.colors.primary.default,
  },
  actions: {
    width: '100%',
    gap: 10,
  },
  action: {
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: theme.colors.primary.default,
  },
  actionPressed: {
    backgroundColor: theme.colors.primary.dark,
  },
  actionDisabled: {
    opacity: 0.55,
  },
  actionText: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.primary.contrast,
    textAlign: 'center',
  },
});
